import { combineReducers } from '@ngrx/store';
import dayjs from 'dayjs';
import { safeSessionStorage } from 'src/app/safe-web-storage';
import {
  ClientStore,
  EmailMessage,
  Lang,
  LocationValid,
  Offer,
  Order,
  OrderMeta,
  PresetOrderData,
  StoreCatalog,
  StoreViewState,
  StoreRecentsInfo,
  CustomerLoyalty,
  CustomerLoyaltyTrasaction,
} from '../../../stores/stores';
import { ModeSlotsInfo, Slot } from '../types/AvailableSlotsResponse';
import { CustomerProfile, Tokens } from '../types/CustomerSocialLogin';
import { DELIVERY_METHOD_VALUES } from '../types/DeliveryMethod';
import { StandalonePaymentOrder } from '../types/StandalonePayment';
import { UIError } from '../types/UIError';
import { StoreAction, StoreActionType } from './stores.actions';
import { Page } from '../../../api/types/Page';
import DateTimeUtils from '../utils/DateTimeUtils';

// Merge order data while preserving gift cards and order items across polling updates.
// If newOrder has no items: restore entire orderItems array from existing order.
// Otherwise: preserve gift card responses within individual items.
function mergeOrderWithPreserveGiftCards(
  existingOrder: Order | null,
  newOrder: Order,
): Order {
  if (!existingOrder) {
    return newOrder;
  }

  if (!newOrder.orderItems || newOrder.orderItems.length === 0) {
    return {
      ...newOrder,
      orderItems: existingOrder.orderItems,
    };
  }

  const preservedOrderItems = newOrder.orderItems.map((newItem) => {
    const existingItem = existingOrder.orderItems?.find(
      (item) =>
        item.offerId === newItem.offerId &&
        item.variantOfferId === newItem.variantOfferId,
    );
    // If existing item has gift card responses and new item doesn't, preserve them
    if (
      existingItem?.giftCardResponses &&
      existingItem.giftCardResponses.length > 0 &&
      (!newItem.giftCardResponses || newItem.giftCardResponses.length === 0)
    ) {
      return {
        ...newItem,
        giftCardResponses: existingItem.giftCardResponses,
      };
    }
    return newItem;
  });

  return {
    ...newOrder,
    orderItems: preservedOrderItems,
  };
}

export interface SelectedStore {
  selectedStore: SelectedStoreState;
}
export interface SelectedStoreState {
  status: 'INITIAL' | 'LOADING' | 'LOADED' | 'FAILED';
  data: ClientStore;
  firstLoading: true | false;
}

export const selectedStoreInitialState: SelectedStore = {
  selectedStore: {
    status: 'INITIAL',
    firstLoading: false,
    //  data: null,
    data: {
      id: null,
      name: '',
      companyName: '',
      vatNumber: '',
      companyAddress: {
        addressLine1: '',
        addressLine2: '',
        postCode: '',
        region: '',
        city: '',
        country: {
          id: 1,
          name: '',
          code: '',
          phoneCode: '',
          defaultLocale: '',
          europeanCountry: true,
        },
      },
      // image: 'loading.jpg',
      // logo: 'generic-logo.svg',
      description: '',
      coordinates: {
        longitude: 0,
        latitude: 0,
      },
      aliasName: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        postCode: '',
        region: '',
        city: '',
        country: {
          id: -1,
          name: '',
          phoneCode: '',
          europeanCountry: false,
        },
      },
      phoneNumber: '',
      language: {
        id: -1,
        name: '',
        locale: '',
      },
      relation: {
        childStores: [],
        parentStore: null,
        siblingStores: [],
        allDescendantStores: [],
      },
      settings: {},
      externalId: '',
      numberOfLocations: 0,
      numberOfOffers: 0,
      numberOfOrders: 0,
      specialSchedules: [],
      tag: '',
      createdAt: '',
    },
  },
};

export function selectedStore(
  state: SelectedStoreState = selectedStoreInitialState.selectedStore,
  action: StoreAction,
): SelectedStoreState {
  switch (action.type) {
    case StoreActionType.LoadStore:
      return { ...selectedStoreInitialState.selectedStore, status: 'LOADING' };
    case StoreActionType.LoadStoreSuccess:
      return {
        status: 'LOADED',
        data: action.store,
        firstLoading: true,
      };
    case StoreActionType.LoadStoreFailed:
      return { ...selectedStoreInitialState[''], status: 'FAILED' };
    case StoreActionType.LoadStoreSeveralTimes:
      return {
        ...state,
        firstLoading: false,
      };
  }
  return state;
}

const reducerSelectedStore: (
  state: SelectedStore,
  action: StoreAction,
) => SelectedStore = combineReducers({
  selectedStore,
});

export function selectedStoresReducer(
  state: SelectedStore = selectedStoreInitialState,
  action: StoreAction,
): SelectedStore {
  return reducerSelectedStore(state, action);
}

/**
 * CATALOG STATE MANAGEMENT
 */

export interface CatalogList {
  status: 'INITIAL' | 'LOADING' | 'LOADED' | 'FAILED';
  data: StoreCatalog;
  selectedCategory: number;
  selectedLang: string; // for now let us assume BE expects lang locale, not the lang id
  loadedLang: string; // lang locale which was used to load the catalog
  errorMessage: any;
}

export interface CatalogState {
  catalog: Catalog;
}

export interface CatalogLanguagesList {
  status: 'INITIAL' | 'FETCHED' | 'FAILED';
  data: Lang[];
  loadedStoreId: number;
}

export interface Catalog {
  cataloglist: CatalogList;
  availableCatalogLanguages: CatalogLanguagesList;
  selectedStore: SelectedStoreState;
}

export const catalogInitialState: Catalog = {
  cataloglist: {
    status: 'INITIAL',
    data: {
      categories: [],
      offers: [],
      catalogId: -1,
      currency: '',
    },
    selectedCategory: -1,
    selectedLang: null,
    loadedLang: null,
    errorMessage: '',
  },
  availableCatalogLanguages: null,
  selectedStore: null,
};

export function cataloglist(
  state: CatalogList = catalogInitialState.cataloglist,
  action: StoreAction,
): CatalogList {
  switch (action.type) {
    case StoreActionType.LoadStore:
      return catalogInitialState.cataloglist;
    case StoreActionType.SetCatalogLoading:
    case StoreActionType.LoadCatalog:
      return { ...state, status: 'LOADING' };
    case StoreActionType.LoadCatalogSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: action.catalog,
        selectedCategory: null,
        loadedLang: action.lang,
        errorMessage: '',
      };
    case StoreActionType.LoadCatalogFailed:
      return { ...catalogInitialState.cataloglist, status: 'FAILED' };
    case StoreActionType.SelectCategory:
      return { ...state, selectedCategory: action.selectedCategoryId };
    case StoreActionType.SelectCategorySuccess:
      return { ...state };
    case StoreActionType.SelectCategoryFailed:
      return { ...catalogInitialState.cataloglist, selectedCategory: null };
    case StoreActionType.SelectCatalogLanguage:
      return { ...state, selectedLang: action.selectedLanguage };
    case StoreActionType.CheckExistingOrderSuccess: {
      return { ...state, selectedLang: action.order.catalogLanguageLocale };
    }
    default:
      return state;
  }
}

export function availableCatalogLanguages(
  state: CatalogLanguagesList = catalogInitialState.availableCatalogLanguages,
  action: StoreAction,
): CatalogLanguagesList {
  switch (action.type) {
    case StoreActionType.LoadCatalogLanguagesSuccess:
      return {
        status: 'FETCHED',
        data: action.availableLanguageList.data,
        loadedStoreId: action.storeId,
      };
    case StoreActionType.LoadCatalogLanguagesFailed:
      return {
        ...state,
        status: 'FAILED',
      };
  }
  return state;
}

const reducerCatalog: (state: Catalog, action: StoreAction) => Catalog =
  combineReducers({
    cataloglist,
    availableCatalogLanguages,
    selectedStore,
  });

export function storesCatalogReducer(
  state: Catalog = catalogInitialState,
  action: StoreAction,
): Catalog {
  return reducerCatalog(state, action);
}

// EOF: catalog state management

/**
 * LINK CODE
 */

export interface LinkCodeState {
  status: 'INITIAL' | 'LOADED' | 'SET' | 'FAILED';
  data: PresetOrderData;
}

export interface LinkCode {
  linkCodeState: LinkCodeState;
}

export const linkCodeInitialState: LinkCode = {
  linkCodeState: {
    status: 'INITIAL',
    data: null,
  },
};

export function linkCodeState(
  state: LinkCodeState = linkCodeInitialState.linkCodeState,
  action: StoreAction,
): LinkCodeState {
  switch (action.type) {
    case StoreActionType.LoadLinkCodeSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: action.linkCodeData,
      };
    case StoreActionType.LoadLinkCodeFailed:
      return {
        ...state,
        status: 'FAILED',
      };
    case StoreActionType.LinkCodeSet:
      return {
        ...state,
        status: 'SET',
      };
  }
  return state;
}

const reducerLinkCodeState: (state: LinkCode, action: StoreAction) => LinkCode =
  combineReducers({
    linkCodeState,
  });

export function linkCodeStateReducer(
  state: LinkCode = linkCodeInitialState,
  action: StoreAction,
): LinkCode {
  return reducerLinkCodeState(state, action);
}
// EOF: Link code

/**
 * STORED CUSTOMER DATA
 */
export interface StoredCustomerState {
  status: 'INITIAL' | 'LOADED' | 'SET' | 'FAILED';
  data: PresetOrderData;
}

export interface StoredCustomerStateData {
  storedCustomerState: StoredCustomerState;
}

export const storedCustomerDataInitialState: StoredCustomerStateData = {
  storedCustomerState: {
    status: 'INITIAL',
    data: null,
  },
};

export function storedCustomerState(
  state: StoredCustomerState = storedCustomerDataInitialState.storedCustomerState,
  action: StoreAction,
): StoredCustomerState {
  switch (action.type) {
    case StoreActionType.LoadStoredCustomerDataSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: action.storedCustomerData,
      };
    case StoreActionType.LoadStoredCustomerDataFailed:
      return {
        ...state,
        status: 'FAILED',
      };
    case StoreActionType.StoredCustomerDataSet:
      return {
        ...state,
        status: 'SET',
      };
  }
  return state;
}

const reducerStoredCustomerState: (
  state: StoredCustomerStateData,
  action: StoreAction,
) => StoredCustomerStateData = combineReducers({
  storedCustomerState,
});

export function storedCustomerStateReducer(
  state: StoredCustomerStateData = storedCustomerDataInitialState,
  action: StoreAction,
): StoredCustomerStateData {
  return reducerStoredCustomerState(state, action);
}
// EOF: Stored customer data

/**
 * VIEW OFFER ITEM DETAILS
 */
export interface OfferItem {
  offerItemState: OfferItemState;
}

export interface OfferItemState {
  status: 'INITIAL' | 'LOADING' | 'LOADED' | 'FAILED';
  selectedOrderItemUuid: string;
  data: Offer;
}

export const offerItemInitialState: OfferItem = {
  offerItemState: {
    status: 'INITIAL',
    selectedOrderItemUuid: null,
    data: null,
  },
};

export function offerItemState(
  state: OfferItemState = offerItemInitialState.offerItemState,
  action: StoreAction,
): OfferItemState {
  switch (action.type) {
    case StoreActionType.InitializeOrderItem:
      return { ...offerItemInitialState[''], status: 'INITIAL' };
    case StoreActionType.ViewOrderItem:
      return {
        ...state,
        selectedOrderItemUuid: action.selectedItemOrderUuid,
        status: 'LOADING',
        data: null,
      };
    case StoreActionType.ViewOrderItemSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: action.offerItem,
      };
    case StoreActionType.ViewOrderItemFailed:
      return {
        ...offerItemInitialState[''],
        status: 'FAILED',
      };
  }
  return state;
}

const reducerOfferItemState: (
  state: OfferItem,
  action: StoreAction,
) => OfferItem = combineReducers({
  offerItemState,
});

export function offerItemStateReducer(
  state: OfferItem = offerItemInitialState,
  action: StoreAction,
): OfferItem {
  return reducerOfferItemState(state, action);
}

/**
 * ORDER STATE MANAGEMENT
 */
export interface Cart {
  cartState: CartState;
}

export interface CartState {
  status:
    | 'INITIAL'
    | 'LOADING'
    | 'CHECKEXISTING'
    | 'REFRESH'
    | 'REFRESHED'
    | 'LOADED'
    | 'ITEMADDING'
    | 'ITEMADDED'
    | 'ITEMREMOVING'
    | 'ITEMREMOVED'
    | 'RULE_OFFERS_REMOVED'
    | 'FAILED'
    | 'ITEMADDFAILED'
    | 'ITEMREMOVEFAILED'
    | 'ITEMUPDATING'
    | 'ITEMUPDATED'
    | 'ITEMUPDATEFAILED'
    | 'ORDERUPDATING'
    | 'ORDERUPDATEFAILED'
    | 'CHECKEXISTINGFAILED'
    | 'ORDERSUBMITTED'
    | 'ORDER_PENDING_CONFIRMATION'
    | 'ORDERSUBMITFAILED'
    | 'INIT_ONLINE_PAYMENT'
    | 'FINISHED_ONLINE_PAYMENT'
    | 'RULE_OFFERS_ADDED'
    | 'RULE_OFFERS_ADD_FAILED';
  data: Order;
  errorCode?: string;
}

export const cartInitialState: Cart = {
  cartState: {
    status: 'INITIAL',
    data: {
      location: -1,
      uuid: null,
      orderItems: [],
    },
  },
};

export function cartState(
  state: CartState = cartInitialState.cartState,
  action: StoreAction,
): CartState {
  switch (action.type) {
    case StoreActionType.LoadStore:
    case StoreActionType.ResetOrder:
      return cartInitialState.cartState;
    case StoreActionType.InitializeOrder:
      return { ...state, status: 'LOADING' };
    case StoreActionType.InitializeOrderSuccess:
      return {
        status: 'LOADED',
        data: action.order,
      };
    case StoreActionType.InitializeOrderFailed:
      return { ...cartInitialState.cartState, status: 'FAILED' };
    case StoreActionType.InitializeCartState:
      return { ...cartInitialState.cartState, status: 'INITIAL' };
    case StoreActionType.CheckExistingOrder:
      let data = state.data;
      if (action.orderUuid !== state.data.uuid) {
        // Preserve existing cart items during verification to prevent flickering
        // Only clear cart data if there are no items, otherwise keep items during verification
        if (!state.data.orderItems || state.data.orderItems.length === 0) {
          data = cartInitialState.cartState.data;
        } else {
          // Keep existing cart data but update the UUID for verification
          data = { ...state.data, uuid: action.orderUuid };
        }
      }
      return { ...state, status: action.cartIntent, data };
    case StoreActionType.CheckExistingOrderSuccess:
      let newStatus: CartState['status'] = 'LOADED';
      switch (state.status) {
        case 'REFRESH':
          newStatus = 'REFRESHED';
          break;
      }
      return {
        status: newStatus,
        data: action.order,
      };
    case StoreActionType.CheckExistingOrderFailed:
      // If order check fails but we have items in cart, preserve them
      // This prevents cart from being cleared when verification fails after page refresh
      if (state.data?.orderItems?.length > 0) {
        return {
          ...state,
          status: 'LOADED', // Keep cart as loaded if it has items
        };
      }
      return {
        ...selectedStoreInitialState[''],
        status: 'CHECKEXISTINGFAILED',
      };
    case StoreActionType.AddOrderItem:
    case StoreActionType.AddRuleOrderItem:
      return { ...state, status: 'ITEMADDING' };
    case StoreActionType.AddOrderItemSuccess:
      return { ...state, status: 'ITEMADDED' };
    case StoreActionType.AddRuleOrderItemSuccess:
      return { ...state, status: 'RULE_OFFERS_ADDED' };
    case StoreActionType.AddOrderItemFailed:
      return { ...state, status: 'ITEMADDFAILED' };
    case StoreActionType.AddRuleOrderItemFailed:
      return { ...state, status: 'RULE_OFFERS_ADD_FAILED' };
    // Deliberately not StoreActionType.UpdateOrderItem: that fires on every
    // tap, before the debounce in onUpdateOrderItem has settled. Keying off
    // UpdateOrderItemSyncStarted (dispatched by the effect right before the
    // HTTP call) means 'ITEMUPDATING' only appears once a real request is
    // about to go out, not on every rapid tap still being collected (#10228).
    case StoreActionType.UpdateOrderItemSyncStarted:
      return { ...state, status: 'ITEMUPDATING' };
    case StoreActionType.UpdateOrderItemSuccess:
      return { ...state, status: 'ITEMUPDATED' };
    case StoreActionType.UpdateOrderItemFailed:
      return { ...state, status: 'ITEMUPDATEFAILED' };
    case StoreActionType.RemoveOrderItem:
    case StoreActionType.RemoveRuleOrderItem:
      return { ...state, status: 'ITEMREMOVING' };
    case StoreActionType.RemoveOrderItemSuccess:
      return { ...state, status: 'ITEMREMOVED' };
    case StoreActionType.RemoveRuleOrderItemSuccess:
      return { ...state, status: 'RULE_OFFERS_REMOVED' };
    case StoreActionType.RemoveOrderItemFailed:
      return { ...state, status: 'ITEMREMOVEFAILED' };
    case StoreActionType.OrderUpdate:
      return { ...state, status: 'ORDERUPDATING' };
    case StoreActionType.OrderUpdateSuccess:
      return {
        ...state,
        data: { ...state.data, ...action.order },
        status: 'LOADED',
      };
    case StoreActionType.RemoveVoucher:
    case StoreActionType.ValidateVoucherFailed: {
      const newSt = { ...state };
      delete newSt.data.voucherCode;
      delete newSt.data.voucherDiscount;
      delete newSt.data.voucherDiscountType;
      return newSt;
    }
    case StoreActionType.CartStatusUpdate:
      return {
        ...state,
        status: action.cartStatus,
      };
    case StoreActionType.SubmitOrder:
      return {
        ...state,
        status: 'LOADING',
      };
    case StoreActionType.SubmitOrderSuccess:
      return {
        ...state,
        status: 'ORDERSUBMITTED',
        // data: action.order,
      };
    case StoreActionType.SubmitOrderPendingConfirmation:
      return {
        ...state,
        status: 'ORDER_PENDING_CONFIRMATION',
      };
    case StoreActionType.SubmitOrderFailed:
      return {
        ...state,
        status: 'ORDERSUBMITFAILED',
        // data: action.order,
      };
    case StoreActionType.UpdateOrderItemQuantities:
      return {
        ...state,
        status: 'ITEMUPDATING',
      };
    case StoreActionType.UpdateOrderItemQuantitiesSuccess:
      return {
        ...state,
        status: 'ITEMUPDATED',
      };
    case StoreActionType.UpdateOrderItemQuantitiesFailed:
      return {
        ...state,
        status: 'ITEMUPDATEFAILED',
      };
    case StoreActionType.OrderUpdateStatus:
      return {
        ...state,
        status: 'LOADING',
      };
    case StoreActionType.OrderUpdateStatusSuccess:
      const newState = { ...state };
      newState.data.status = 'SUBMITTED';
      newState.status = 'LOADED';
      return newState;
    case StoreActionType.OrderUpdateFailed:
      return { ...state, status: 'ORDERUPDATEFAILED' };
  }
  return state;
}

const reducerCartState: (state: Cart, action: StoreAction) => Cart =
  combineReducers({
    cartState,
  });

export function cartStateReducer(
  state: Cart = cartInitialState,
  action: StoreAction,
): Cart {
  return reducerCartState(state, action);
}

// EOF: order state management

/**
 * ORDER STATUS MANAGEMENT
 * fe can view status of any order by passing orderUuid in the url params
 */

export interface OrderStatus {
  orderStatus: CartState;
}

export const OrderStatusInitialState: OrderStatus = {
  orderStatus: {
    status: 'INITIAL',
    data: {
      location: -1,
      uuid: null,
      orderItems: [],
      createdAt: '',
    },
  },
};

export function orderStatus(
  state: CartState = cartInitialState.cartState,
  action: StoreAction,
): CartState {
  switch (action.type) {
    case StoreActionType.InitializeOrder:
      return { ...state, status: 'LOADING' };
    case StoreActionType.InitializeOrderSuccess:
      return {
        status: 'LOADED',
        data: action.order,
      };
    case StoreActionType.InitializeOrderFailed:
      return { ...selectedStoreInitialState[''], status: 'FAILED' };
    case StoreActionType.InitializeCartState:
      return { ...selectedStoreInitialState[''], status: 'INITIAL' };
    case StoreActionType.ViewOrderStatus:
      return { ...state, status: 'LOADING' };
    case StoreActionType.ViewOrderStatusSuccess:
      return {
        status: 'LOADED',
        data: mergeOrderWithPreserveGiftCards(state.data, action.order),
      };
    case StoreActionType.ViewOrderStatusFailed:
      return { ...selectedStoreInitialState[''], status: 'FAILED' };
    // case StoreActionType.CheckExistingOrder:
    //   return { ...state, status: action.cartIntent };
    // case StoreActionType.CheckExistingOrderSuccess:
    //   let newStatus: CartState['status'] = 'LOADED';
    //   switch (state.status) {
    //     case 'REFRESH':
    //       newStatus = 'REFRESHED';
    //       break;
    //   }
    //   return {
    //     status: newStatus,
    //     data: action.order,
    //   };
  }
  return state;
}

const reducerOrderStatus: (
  state: OrderStatus,
  action: StoreAction,
) => OrderStatus = combineReducers({
  orderStatus,
});

export function orderStatusReducer(
  state: OrderStatus = OrderStatusInitialState,
  action: StoreAction,
): OrderStatus {
  return reducerOrderStatus(state, action);
}

// EOF: order state management

/**
 * VIEW STATE MANAGEMENT
 */
export interface View {
  viewState: ViewState;
}

/**
 * Story #10478 - the parent's shared item reference held while a selector-mode
 * parent shows its child selector, carried across the redirect into the branch.
 *
 * Story #11520 - the same slot now carries either a shared item or a shared
 * category: a link carries one fragment, so exactly one of the two ids is set
 * and the two kinds never coexist.
 */
export interface PendingSharedItemReference {
  parentStoreId: number;
  parentOfferId?: number | null;
  parentCategoryId?: number | null;
}

/**
 * Story #11520 - which kind of reference the pending slot holds, so the carry
 * points and the resolver do not each re-derive it.
 */
export function sharedReferenceKind(
  reference: PendingSharedItemReference | null,
): 'offer' | 'category' | null {
  if (Number.isFinite(reference?.parentOfferId)) {
    return 'offer';
  }
  if (Number.isFinite(reference?.parentCategoryId)) {
    return 'category';
  }
  return null;
}

export interface ViewState {
  status: StoreViewState;
  ulang: string;
  // Story #10478 - null unless a shared item deep-link is pending on a parent.
  pendingSharedItem?: PendingSharedItemReference | null;
}

/**
 * Story #10478 - sessionStorage key mirroring the pending shared item reference so
 * a refresh on the multi-store selector does not lose the deep link.
 */
export const PENDING_SHARED_ITEM_STORAGE_KEY = 'pendingSharedItem';

function readPendingSharedItemFromSession(): PendingSharedItemReference | null {
  try {
    const raw = safeSessionStorage.getItem(PENDING_SHARED_ITEM_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    // Story #11520 - either kind of reference is accepted, and exactly one of the
    // two ids must be present.
    if (parsed && Number.isFinite(parsed.parentStoreId)) {
      if (Number.isFinite(parsed.parentOfferId)) {
        return {
          parentStoreId: parsed.parentStoreId,
          parentOfferId: parsed.parentOfferId,
        };
      }
      if (Number.isFinite(parsed.parentCategoryId)) {
        return {
          parentStoreId: parsed.parentStoreId,
          parentCategoryId: parsed.parentCategoryId,
        };
      }
    }
  } catch {
    // ignore a malformed value - the reference is best-effort only
  }
  return null;
}

function writePendingSharedItemToSession(
  reference: PendingSharedItemReference | null,
): void {
  if (reference) {
    safeSessionStorage.setItem(
      PENDING_SHARED_ITEM_STORAGE_KEY,
      JSON.stringify(reference),
    );
  } else {
    safeSessionStorage.removeItem(PENDING_SHARED_ITEM_STORAGE_KEY);
  }
}

export const viewInitialState: View = {
  viewState: {
    status: {
      state: 'LOADING',
    },
    ulang: 'en',
    // hydrate from sessionStorage so a refresh on the selector keeps the deep link
    pendingSharedItem: readPendingSharedItemFromSession(),
  },
};

export function viewState(
  state: ViewState = viewInitialState.viewState,
  action: StoreAction,
): ViewState {
  switch (action.type) {
    case StoreActionType.InitialViewState:
      return { ...viewInitialState[''] };
    case StoreActionType.ViewStateUpdate:
      return { ...state, status: action.viewState };
    case StoreActionType.ViewStateUpdateSuccess:
      return {
        ...state,
      };
    case StoreActionType.ViewStateUpdateFailed:
      return { ...viewInitialState[''] };
    case StoreActionType.ViewStateUpdateUserLanguage: {
      const newState = { ...state };
      newState.ulang = action.ulang;
      return newState;
    }
    case StoreActionType.SetPendingSharedItem: {
      const pendingSharedItem: PendingSharedItemReference = {
        parentStoreId: action.parentStoreId,
        parentOfferId: action.parentOfferId,
      };
      writePendingSharedItemToSession(pendingSharedItem);
      return { ...state, pendingSharedItem };
    }
    // Story #11520 - the category twin: the same slot, the other id, so setting one
    // kind replaces the other and the two can never coexist.
    case StoreActionType.SetPendingSharedCategory: {
      const pendingSharedItem: PendingSharedItemReference = {
        parentStoreId: action.parentStoreId,
        parentCategoryId: action.parentCategoryId,
      };
      writePendingSharedItemToSession(pendingSharedItem);
      return { ...state, pendingSharedItem };
    }
    case StoreActionType.ClearPendingSharedItem: {
      writePendingSharedItemToSession(null);
      return { ...state, pendingSharedItem: null };
    }
  }
  return state;
}

const reducerViewState: (state: View, action: StoreAction) => View =
  combineReducers({
    viewState,
  });

export function viewStateReducer(
  state: View = viewInitialState,
  action: StoreAction,
): View {
  return reducerViewState(state, action);
}

// EOF: order state management

/**
 * possibly temporary solution for passing the order meta data
 * between controllers/screens. if Rigas decides to update the meta
 * fields on focus out to maintain persistancy then this is not needed
 */
// ORDER META

export interface OrderMetaData {
  orderMetaState: OrderMetaState;
}

export interface MultidayDetails {
  displayMultidayDetails: boolean;
  slotMap: { [date: string]: Slot[] };
  selectedSlots: { [date: string]: Slot };
  selectedAddresses?: { [date: string]: number };
}

export interface OrderMetaState {
  status: 'INITIAL' | 'INPROGRESS' | 'FINISHED' | 'FINISHED_ONLINE_PAYMENT';
  data: OrderMeta;
  storeOpeningInfo: {
    slots: ModeSlotsInfo;
    openInDate: boolean;
    date: string;
    deliveryMethod: string;
  };
  wish: Date;
  multidayDetails: MultidayDetails;
}

export const orderMetaInitialState: OrderMetaState = {
  status: 'INITIAL',
  data: {},
  storeOpeningInfo: {
    slots: {
      availableSlots: [],
      selectedSlot: {
        startTime: '',
        endTime: '',
        isDisabled: true,
        totalOrders: 0,
      },
      slotDurationEnabled: false,
    },
    date: null,
    openInDate: null,
    deliveryMethod: null,
  },
  wish: null,
  multidayDetails: {
    displayMultidayDetails: false,
    slotMap: {},
    selectedSlots: {},
    selectedAddresses: {},
  },
};

// Static mapping from ApplyPresetOrderMeta preset keys to order-meta keys.
// Hoisted to module scope so it is not rebuilt on every action.
const PRESET_TO_META_KEY: { [presetKey: string]: string } = {
  comment: 'comment',
  customerEmail: 'customerEmail',
  customerName: 'customerName',
  customerPhoneNumber: 'customerPhoneNumber',
  deliveryStreetAddress: 'customerStreet',
  deliveryPostCode: 'customerZip',
  deliveryCity: 'customerTown',
  deliveryComment: 'deliveryComment',
  deliveryHouseNo: 'deliveryHouseNo',
  floorNumber: 'customerFloor',
  doorbell: 'doorbell',
  latitude: 'latitude',
  longitude: 'longitude',
  pickupStreetAddress: 'pickupStreet',
  pickupPostCode: 'pickupZip',
  pickupCity: 'pickupTown',
  pickupFloorNumber: 'pickupFloor',
  pickupDoorbell: 'pickupDoorbell',
  pickupLatitude: 'pickupLatitude',
  pickupLongitude: 'pickupLongitude',
  externalOrderId: 'externalOrderId',
};

export function orderMetaState(
  state: OrderMetaState = orderMetaInitialState,
  action: StoreAction,
): OrderMetaState {
  switch (action.type) {
    case StoreActionType.InitializeOrderMeta:
      return { ...state, status: 'INITIAL' };
    case StoreActionType.AddOrderMeta: {
      // A dispatch that would not change anything returns the SAME state object.
      //
      // Reducers are the right place to decide whether state changed — a
      // component cannot know whether its value is already the current one, and
      // guarding at each of the many dispatch sites would have to be repeated
      // and kept in sync forever. Returning a fresh object regardless is not
      // merely wasteful: a new reference wakes every selector downstream, and
      // via the order-update effects it costs a PUT, a CheckExistingOrder and a
      // full catalog re-render for a value that never moved.
      const isUnchanged =
        action.value != null
          ? state.data[action.key] === action.value &&
            state.status === 'INPROGRESS'
          : !Object.prototype.hasOwnProperty.call(state.data, action.key);
      if (isUnchanged) {
        return state;
      }

      const newState = { ...state, data: { ...state.data } };
      if (action.value != null) {
        newState.data[action.key] = action.value;
        newState.status = 'INPROGRESS';
      } else {
        delete newState.data[action.key];
      }
      return newState;
    }
    case StoreActionType.ApplyPresetOrderMeta: {
      const newState = { ...state, data: { ...state.data } };
      let applied = false;
      Object.entries(PRESET_TO_META_KEY).forEach(([presetKey, metaKey]) => {
        const value = action.presetData?.[presetKey];
        if (value != null) {
          newState.data[metaKey] = value;
          applied = true;
        }
      });
      // Only flip status when something actually landed, mirroring AddOrderMeta.
      if (applied) {
        newState.status = 'INPROGRESS';
      }
      return newState;
    }
    case StoreActionType.UpdateCustomerDetailsInOrderMetaState:
      return {
        ...state,
        data: {
          ...state.data,
          customerName: action.customerDetails.userName,
          customerEmail: action.customerDetails.email,
          customerPhoneNumber: action.customerDetails.phoneNumber,
        },
        status: 'INPROGRESS',
      };
    case StoreActionType.ClearOrderMeta:
      const cleanState: OrderMetaState = { ...orderMetaInitialState };
      // set new value if passed (usually expected this to be the location value)
      Object.keys(action.settingsToKeep).forEach(
        (s) => (cleanState.data[s] = action.settingsToKeep[s]),
      );
      return cleanState;
    case StoreActionType.UpdateOrderMetaStatus:
      return { ...state, status: action.status };
    case StoreActionType.UpdateMultidayDetails:
      return {
        ...state,
        multidayDetails: {
          ...state.multidayDetails,
          displayMultidayDetails: action.displayMultidayDetails,
        },
      };
    case StoreActionType.UpdateMultidaySlotMap:
      return {
        ...state,
        multidayDetails: {
          ...state.multidayDetails,
          slotMap: {
            ...state.multidayDetails.slotMap,
            [action.date]: action.slots,
          },
        },
      };
    case StoreActionType.UpdateMultidaySelectedSlot:
      return {
        ...state,
        multidayDetails: {
          ...state.multidayDetails,
          selectedSlots: {
            ...state.multidayDetails.selectedSlots,
            [action.date]: action.slot,
          },
        },
      };
    case StoreActionType.UpdateMultidaySelectedAddress:
      return {
        ...state,
        multidayDetails: {
          ...state.multidayDetails,
          selectedAddresses: {
            ...state.multidayDetails.selectedAddresses,
            [action.date]: action.addressId,
          },
        },
      };
    case StoreActionType.ClearMultidaySelectedAddresses:
      return {
        ...state,
        multidayDetails: {
          ...state.multidayDetails,
          selectedAddresses: {},
        },
      };
    case StoreActionType.CleanupMultidayObsoleteDates:
      const validDatesSet = new Set(action.validDates);
      const cleanedSlots = Object.keys(
        state.multidayDetails.selectedSlots || {},
      )
        .filter((date) => validDatesSet.has(date))
        .reduce(
          (acc, date) => {
            acc[date] = state.multidayDetails.selectedSlots[date];
            return acc;
          },
          {} as { [date: string]: any },
        );

      const cleanedAddresses = Object.keys(
        state.multidayDetails.selectedAddresses || {},
      )
        .filter((date) => validDatesSet.has(date))
        .reduce(
          (acc, date) => {
            acc[date] = state.multidayDetails.selectedAddresses[date];
            return acc;
          },
          {} as { [date: string]: number },
        );

      return {
        ...state,
        multidayDetails: {
          ...state.multidayDetails,
          selectedSlots: cleanedSlots,
          selectedAddresses: cleanedAddresses,
        },
      };
    case StoreActionType.FetchSlotsFailed:
      return {
        ...state,
        storeOpeningInfo: { ...orderMetaInitialState.storeOpeningInfo },
      };
    case StoreActionType.FetchSlotsSuccess: {
      const wishTimeDate = DateTimeUtils.normalizeDateOnly(state.data.wishTime);
      const hasOpeningInfoDate = !!state.storeOpeningInfo.date;
      const hasWishTimeMismatch =
        !!wishTimeDate && action.response.date !== wishTimeDate;
      const hasNoPriorDateContext = !hasOpeningInfoDate && !wishTimeDate;

      if (
        action.requestDate &&
        action.requestDate !== action.response.date &&
        (hasNoPriorDateContext || hasOpeningInfoDate || hasWishTimeMismatch)
      ) {
        return {
          ...state,
          storeOpeningInfo: {
            slots: {
              ...orderMetaInitialState.storeOpeningInfo.slots,
            },
            openInDate: false,
            date: action.requestDate,
            deliveryMethod: action.deliveryMode,
          },
        };
      }

      switch (action.deliveryMode) {
        case 'IN_STORE_LOCATION':
          return {
            ...state,
            storeOpeningInfo: {
              slots: {
                availableSlots: action.response.inStoreLocation.availableSlots,
                selectedSlot: getSelectedSlot(
                  action.response.inStoreLocation.selectedSlot,
                  action.response.inStoreLocation.availableSlots,
                  state.data.wishTime,
                ),
                slotDurationEnabled:
                  action.response.inStoreLocation.slotDurationEnabled,
              },
              date: action.response.date,
              openInDate:
                action.response.inStoreLocation.availableSlots.length > 0 &&
                !!action.response.inStoreLocation.selectedSlot,
              deliveryMethod: action.deliveryMode,
            },
          };
        case 'NO_LOCATION':
          return {
            ...state,
            storeOpeningInfo: {
              slots: {
                availableSlots: action.response.noLocation.availableSlots,
                selectedSlot: getSelectedSlot(
                  action.response.noLocation.selectedSlot,
                  action.response.noLocation.availableSlots,
                  state.data.wishTime,
                ),
                slotDurationEnabled:
                  action.response.noLocation.slotDurationEnabled,
              },
              date: action.response.date,
              openInDate:
                action.response.noLocation.availableSlots.length > 0 &&
                !!action.response.noLocation.selectedSlot,
              deliveryMethod: action.deliveryMode,
            },
          };
        case 'ADDRESS':
          return {
            ...state,
            storeOpeningInfo: {
              slots: {
                availableSlots: action.response.address.availableSlots,
                selectedSlot: getSelectedSlot(
                  action.response.address.selectedSlot,
                  action.response.address.availableSlots,
                  state.data.wishTime,
                ),
                slotDurationEnabled:
                  action.response.address.slotDurationEnabled,
              },
              date: action.response.date,
              openInDate:
                action.response.address.availableSlots.length > 0 &&
                !!action.response.address.selectedSlot,
              deliveryMethod: action.deliveryMode,
            },
          };
        default:
          return {
            ...state,
            storeOpeningInfo: { ...orderMetaInitialState.storeOpeningInfo },
          };
      }
    }
    case StoreActionType.OrderUpdateSuccess: {
      const { location, latitude, longitude, pickupLatitude, pickupLongitude } =
        action.order;
      const isAdminOrderUpdate = window.location.href.includes('/orders/');
      let newState = { ...state, data: { ...state.data } };
      if (action.order.totalDiscountedPrice === 0)
        newState = { ...state, data: { ...state.data } };
      const hasPreviousOrderLookupLocation =
        !!newState.data.PREVIOUS_ORDER_ID &&
        newState.data.location !== undefined &&
        newState.data.location !== null &&
        newState.data.location !== '';
      if (!hasPreviousOrderLookupLocation) {
        newState.data.location = location?.toString();
      }
      // Symmetric with the pickup guard below. OrderUtils.mapValue omits falsy
      // coordinates from the request; the v1 PUT mapper cannot tell an absent
      // field from an explicit null and so nulls the column; the response then
      // echoes null back here. Writing that echo unguarded made the erasure
      // self-sustaining — the next request omits the coordinate again and the
      // P&D driving-distance fee stays at 0.
      // An older order reply must not replace a newer client address, including
      // a text address whose coordinates are still being resolved.
      if (isAdminOrderUpdate || !newState.data.addressId) {
        if (latitude != null) {
          newState.data.latitude = latitude;
        }
        if (longitude != null) {
          newState.data.longitude = longitude;
        }
      }
      if (isAdminOrderUpdate || !newState.data.pickupAddressId) {
        if (pickupLatitude != null) {
          newState.data.pickupLatitude = pickupLatitude;
        }
        if (pickupLongitude != null) {
          newState.data.pickupLongitude = pickupLongitude;
        }
      }

      return newState;
    }
    case StoreActionType.InitializeOrderSuccess:
    case StoreActionType.CheckExistingOrderSuccess: {
      const {
        customerUserId,
        customerEmail,
        customerName,
        customerPhoneNumber,
        pickupCity,
        pickupPostCode,
        pickupStreetAddress,
        deliveryCity,
        deliveryPostCode,
        deliveryStreetAddress,
        deliveryHouseNo,
        floorNumber,
        doorbell,
        comment,
        deliveryMethod,
        wishTime,
        location,
        latitude,
        longitude,
        pickupLatitude,
        pickupLongitude,
        voucherCode,
        deliveryComment,
        billingStreetAddress,
        billingHouseNo,
        billingPostCode,
        billingCity,
        billingState,
        billingCountryId,
        billingVatNumber,
        tip,
        properties,
      } = action.order;
      const isAdminOrderUpdate = window.location.href.includes('/orders/');
      const newState = { ...state, data: { ...state.data } };

      // Load consumption mode from order properties
      if (properties?.CONSUMPTION_MODE) {
        newState.data.CONSUMPTION_MODE = properties.CONSUMPTION_MODE;
      }
      if (
        (!newState.data.customerUserId || isAdminOrderUpdate) &&
        customerUserId !== undefined
      ) {
        newState.data.customerUserId = customerUserId;
      }
      if (
        (!newState.data.customerEmail || isAdminOrderUpdate) &&
        customerEmail
      ) {
        newState.data.customerEmail = customerEmail;
      }
      if ((!newState.data.customerName || isAdminOrderUpdate) && customerName) {
        newState.data.customerName = customerName;
      }
      if (
        (!newState.data.customerPhoneNumber || isAdminOrderUpdate) &&
        customerPhoneNumber
      ) {
        newState.data.customerPhoneNumber = customerPhoneNumber;
      }
      if ((!newState.data.customerTown || isAdminOrderUpdate) && deliveryCity) {
        newState.data.customerTown = deliveryCity;
      }
      if (
        (!newState.data.customerZip || isAdminOrderUpdate) &&
        deliveryPostCode
      ) {
        newState.data.customerZip = deliveryPostCode;
      }
      if (
        (!newState.data.customerStreet || isAdminOrderUpdate) &&
        deliveryStreetAddress
      ) {
        newState.data.customerStreet = deliveryStreetAddress;
      }
      if ((!newState.data.pickupTown || isAdminOrderUpdate) && pickupCity) {
        newState.data.pickupTown = pickupCity;
      }
      if ((!newState.data.pickupZip || isAdminOrderUpdate) && pickupPostCode) {
        newState.data.pickupZip = pickupPostCode;
      }
      if (
        (!newState.data.pickupStreet || isAdminOrderUpdate) &&
        pickupStreetAddress
      ) {
        newState.data.pickupStreet = pickupStreetAddress;
      }
      if (
        (!newState.data.deliveryHouseNo || isAdminOrderUpdate) &&
        deliveryHouseNo
      ) {
        newState.data.deliveryHouseNo = deliveryHouseNo;
      }
      if ((!newState.data.customerFloor || isAdminOrderUpdate) && floorNumber) {
        newState.data.customerFloor = floorNumber;
      }
      if ((!newState.data.doorbell || isAdminOrderUpdate) && doorbell) {
        newState.data.doorbell = doorbell;
      }
      if (
        (!newState.data.billingStreetAddress || isAdminOrderUpdate) &&
        billingStreetAddress
      ) {
        newState.data.billingStreetAddress = billingStreetAddress;
      }
      if (
        (!newState.data.billingHouseNo || isAdminOrderUpdate) &&
        billingHouseNo
      ) {
        newState.data.billingHouseNo = billingHouseNo;
      }
      if (
        (!newState.data.billingPostCode || isAdminOrderUpdate) &&
        billingPostCode
      ) {
        newState.data.billingPostCode = billingPostCode;
      }
      if ((!newState.data.billingCity || isAdminOrderUpdate) && billingCity) {
        newState.data.billingCity = billingCity;
      }
      if ((!newState.data.billingState || isAdminOrderUpdate) && billingState) {
        newState.data.billingState = billingState;
      }
      if (
        (!newState.data.billingCountryId || isAdminOrderUpdate) &&
        billingCountryId
      ) {
        newState.data.billingCountryId = billingCountryId;
      }
      if (
        (!newState.data.billingVatNumber || isAdminOrderUpdate) &&
        billingVatNumber
      ) {
        newState.data.billingVatNumber = billingVatNumber;
      }
      if ((!newState.data.comment || isAdminOrderUpdate) && comment) {
        newState.data.comment = comment;
      }
      if ((!newState.data.location || isAdminOrderUpdate) && location) {
        newState.data.location = location.toString();
      }
      if (isAdminOrderUpdate || !newState.data.addressId) {
        if ((!newState.data.latitude || isAdminOrderUpdate) && latitude) {
          newState.data.latitude = latitude;
        }
        if ((!newState.data.longitude || isAdminOrderUpdate) && longitude) {
          newState.data.longitude = longitude;
        }
      }
      if (isAdminOrderUpdate || !newState.data.pickupAddressId) {
        if (
          (!newState.data.pickupLatitude || isAdminOrderUpdate) &&
          pickupLatitude
        ) {
          newState.data.pickupLatitude = pickupLatitude;
        }
        if (
          (!newState.data.pickupLongitude || isAdminOrderUpdate) &&
          pickupLongitude
        ) {
          newState.data.pickupLongitude = pickupLongitude;
        }
      }
      if ((!newState.data.voucherCode || isAdminOrderUpdate) && voucherCode) {
        newState.data.voucherCode = voucherCode;
      }
      if (
        (!newState.data.deliveryComment || isAdminOrderUpdate) &&
        deliveryComment
      ) {
        newState.data.deliveryComment = deliveryComment;
      }
      if (deliveryMethod && DELIVERY_METHOD_VALUES[deliveryMethod]) {
        newState.data.deliveryMethod = DELIVERY_METHOD_VALUES[deliveryMethod];
      }
      if (wishTime && dayjs(new Date(wishTime)) >= dayjs()) {
        newState.data.wishTime = wishTime;
      }
      if (tip) {
        newState.data.tip = tip;
      }
      return newState;
    }
    case StoreActionType.UpdateOrderWishTimeSuccess: {
      switch (action.deliveryMethod) {
        case 'IN_STORE_LOCATION':
          return {
            ...state,
            storeOpeningInfo: {
              slots: {
                availableSlots:
                  action.availableSlots.inStoreLocation.availableSlots,
                selectedSlot: action.suggestedSlot,
                slotDurationEnabled:
                  action.availableSlots.inStoreLocation.slotDurationEnabled,
              },
              date: action.availableSlots.date,
              openInDate:
                action.availableSlots.inStoreLocation.availableSlots.length >
                  0 && !!action.availableSlots.inStoreLocation.selectedSlot,
              deliveryMethod: action.deliveryMethod,
            },
          };
        case 'NO_LOCATION':
          return {
            ...state,
            storeOpeningInfo: {
              slots: {
                availableSlots: action.availableSlots.noLocation.availableSlots,
                selectedSlot: action.suggestedSlot,
                slotDurationEnabled:
                  action.availableSlots.noLocation.slotDurationEnabled,
              },
              date: action.availableSlots.date,
              openInDate:
                action.availableSlots.noLocation.availableSlots.length > 0 &&
                !!action.availableSlots.noLocation.selectedSlot,
              deliveryMethod: action.deliveryMethod,
            },
          };
        case 'ADDRESS':
          return {
            ...state,
            storeOpeningInfo: {
              slots: {
                availableSlots: action.availableSlots.address.availableSlots,
                selectedSlot: action.suggestedSlot,
                slotDurationEnabled:
                  action.availableSlots.address.slotDurationEnabled,
              },
              date: action.availableSlots.date,
              openInDate:
                action.availableSlots.address.availableSlots.length > 0 &&
                !!action.availableSlots.address.selectedSlot,
              deliveryMethod: action.deliveryMethod,
            },
          };
        default:
          return {
            ...state,
            storeOpeningInfo: { ...orderMetaInitialState.storeOpeningInfo },
          };
      }
    }
    case StoreActionType.SlotSelected: {
      // Re-selecting the slot that is already selected minted a new
      // storeOpeningInfo AND a new slots object, so getStoreOpeningInfo — which
      // projects by reference — re-emitted to every subscriber. StorePageComponent
      // answers that emission by dispatching order meta, which is how a no-op
      // slot selection turned into an order-update round trip.
      const currentSlot = state.storeOpeningInfo?.slots?.selectedSlot;
      if (
        currentSlot?.startTime === action.selectedSlot?.startTime &&
        currentSlot?.endTime === action.selectedSlot?.endTime
      ) {
        return state;
      }
      return {
        ...state,
        storeOpeningInfo: {
          ...state.storeOpeningInfo,
          slots: {
            ...state.storeOpeningInfo.slots,
            selectedSlot: action.selectedSlot,
          },
        },
      };
    }
    case StoreActionType.UpdateOrderWish:
      return {
        ...state,
        wish: action.wish,
      };
  }
  return state;
}

function getSelectedSlot(
  responseSelectedSlot: Slot,
  availableSlots: Slot[],
  wishTime: string,
) {
  if (!wishTime) {
    return responseSelectedSlot;
  }

  const slotIndex = availableSlots.findIndex((a) =>
    dayjs(a.startTime).isSame(dayjs(wishTime)),
  );
  if (slotIndex !== -1 && !availableSlots[slotIndex].isDisabled) {
    return availableSlots[slotIndex];
  } else {
    return responseSelectedSlot;
  }
}

// const reducerOrderMetaData: (state: OrderMetaData, action: StoreAction) => OrderMetaData = combineReducers({
//   orderMetaState
// });

export function orderMetaDataReducer(
  state: OrderMetaState = orderMetaInitialState,
  action: StoreAction,
): OrderMetaState {
  return orderMetaState(state, action);
}

// EOF: ORDER META

// ERROR STATE

export interface ErrorView {
  errorState: ErrorState;
}

export interface ErrorState {
  status: 'INITIAL' | 'EMITTED';
  errorMessage: string;
  errorMessageParams: any;
  errorCode: string;
  additionalInfo: string[];
  errorPageTitle: string;
  uiErrors: UIError[];
  reference?: string;
  /** See `ErrorMessage.uncaught` (card #11603). */
  uncaught?: boolean;
  /**
   * The uncaught exception on screen was raised again with the same reference
   * (card #11603). CustomErrorHandler does that when a fault keeps firing after
   * something replaced its error page: handing the page over to the menu did
   * not get past the fault.
   */
  raisedAgain?: boolean;
}

export const errorInitialState: ErrorView = {
  errorState: {
    status: 'INITIAL',
    errorMessage: '',
    errorMessageParams: {},
    errorCode: '',
    additionalInfo: [],
    errorPageTitle: null,
    uiErrors: [],
  },
};

export function errorState(
  state: ErrorState = errorInitialState.errorState,
  action: StoreAction,
): ErrorState {
  switch (action.type) {
    case StoreActionType.ErrorMessage:
      return {
        status: 'EMITTED',
        errorMessage: action.errorMessage,
        errorMessageParams: action.errorMessageParams,
        errorCode: action.errorCode,
        additionalInfo: action.additionalInfo,
        errorPageTitle: null,
        uiErrors: [],
        reference: action.reference,
        uncaught: action.uncaught,
        raisedAgain:
          action.uncaught &&
          !!action.reference &&
          state.uncaught === true &&
          action.reference === state.reference,
      };
    case StoreActionType.ErrorMessages:
      return {
        status: 'EMITTED',
        errorMessage: '',
        errorMessageParams: {},
        errorCode: '',
        additionalInfo: [],
        errorPageTitle: action.errorPageTitle,
        uiErrors: action.errors,
      };
    case StoreActionType.ErrorMessageSuccess:
      return { ...state };
    case StoreActionType.ErrorMessageFailed:
      return { ...state, errorMessage: '', additionalInfo: [] };
  }
  return state;
}

const reducerError: (state: ErrorView, action: StoreAction) => ErrorView =
  combineReducers({
    errorState,
  });

export function errorReducer(
  state: ErrorView = errorInitialState,
  action: StoreAction,
): ErrorView {
  return reducerError(state, action);
}

// EOF: ERROR STATE

// STORE LOCATIONS STATE

export interface ValidateStoreLocation {
  storeLocationsState: StoreLocationState;
}

export interface StoreLocationState {
  status: 'INITIAL' | 'LOADING' | 'LOADED';
  locationState: LocationValid;
}

export const storeLocationInitialState: ValidateStoreLocation = {
  storeLocationsState: {
    status: 'INITIAL',
    locationState: null,
  },
};

export function storeLocationsState(
  state: StoreLocationState = storeLocationInitialState.storeLocationsState,
  action: StoreAction,
): StoreLocationState {
  switch (action.type) {
    case StoreActionType.ValidateStoreLocations:
      return {
        ...state,
        status: 'LOADING',
      };
    case StoreActionType.ClearStoreLocation:
      return storeLocationInitialState.storeLocationsState;
    case StoreActionType.ValidateStoreLocationsSuccess:
      return {
        status: 'LOADED',
        locationState: action.locations,
      };
    case StoreActionType.ValidateStoreLocationsFail:
      return { ...state, locationState: null };
  }
  return state;
}

const reducerStoreLocations: (
  state: ValidateStoreLocation,
  action: StoreAction,
) => ValidateStoreLocation = combineReducers({
  storeLocationsState,
});

export function storeLocationsReducer(
  state: ValidateStoreLocation = storeLocationInitialState,
  action: StoreAction,
): ValidateStoreLocation {
  return reducerStoreLocations(state, action);
}

// EOF: STORE LOCATIONS

// ORDER EMAIL STATE

export interface OrderEmail {
  orderEmail: OrderEmailState;
}

export interface OrderEmailState {
  status: 'INITIAL' | 'SENDING' | 'SENT' | 'FAILED';
  emailState: EmailMessage;
  isSilent: boolean;
}

export const orderEmailInitialState: OrderEmail = {
  orderEmail: {
    status: 'INITIAL',
    emailState: null,
    isSilent: false,
  },
};

const orderEmailCurrentState: OrderEmail = orderEmailInitialState;

export function orderEmail(
  state: OrderEmailState = orderEmailInitialState.orderEmail,
  action: StoreAction,
): OrderEmailState {
  switch (action.type) {
    case StoreActionType.SendOrderByEmail:
      orderEmailCurrentState.orderEmail = {
        ...state,
        status: 'SENDING',
        isSilent: action.sendSilent,
      };
      return orderEmailCurrentState.orderEmail;
    case StoreActionType.SendOrderByEmailSuccess:
      if (state.isSilent) {
        // state is silent, returning initial state
        return orderEmailInitialState.orderEmail;
      }
      return {
        ...state,
        status: 'SENT',
        emailState: action.message,
      };
    case StoreActionType.SendOrderByEmailFail:
      if (state.isSilent) {
        // state is silent, returning initial state
        return orderEmailInitialState.orderEmail;
      }
      return {
        ...state,
        status: 'FAILED',
        emailState: null,
      };
    case StoreActionType.ClearOrderByEmail:
      return orderEmailInitialState.orderEmail;
    case StoreActionType.SendStandaloneOrderByEmail:
      orderEmailCurrentState.orderEmail = {
        ...state,
        status: 'SENDING',
      };
      return orderEmailCurrentState.orderEmail;
    case StoreActionType.SendStandaloneOrderByEmailSuccess:
      return {
        ...state,
        status: 'SENT',
        emailState: action.message,
      };
    case StoreActionType.SendStandaloneOrderByEmailFail:
      return {
        ...state,
        status: 'FAILED',
        emailState: null,
      };
  }
  return state;
}

const reducerOrderEmail: (
  state: OrderEmail,
  action: StoreAction,
) => OrderEmail = combineReducers({
  orderEmail,
});

export function orderEmailReducer(
  state: OrderEmail = orderEmailInitialState,
  action: StoreAction,
): OrderEmail {
  return reducerOrderEmail(state, action);
}

// EOF: STORE LOCATIONS

// CHECKOUT COMPONENTS STATE

export interface CheckoutData {
  checkoutState: CheckoutState;
}

export interface CheckoutState {
  status: 'INITIAL' | 'MODIFIED' | 'INVALID_SUBMIT';
  data: any;
}

export const checkoutInitialState: CheckoutData = {
  checkoutState: {
    status: 'INITIAL',
    data: {
      orderBtnDisabled: false,
      personalFormValid: false,
      paymentValid: true, // since paymment is always optional default value would be set to true
      locationInitiallyPersisted: true,
      locationPersistedInMemory: false,
      offerAvailabilityErrors: {
        visible: false,
        offers: [],
      },
      offerOutOfStockErrors: {
        visible: false,
        offers: [],
      },
      ReservableOffersExceededErrors: {
        visible: false,
        offers: [],
      },
      OffersAlreadyReservedErrors: {
        visible: false,
        suggestedSlot: [],
      },
      sameDayOrderingErrors: {
        visible: false,
        errors: [],
      },
      voucherValidation: null,
      voucherDiscount: null,
      voucherDiscountType: null,
      voucherUseType: null,
      voucherOrderMinAmount: null,
      voucherFormattedOrderMinAmount: null,
      voucherHasScheduleRestriction: null,
      voucherHasCategoryRestriction: null,
      slotUnavailableError: {
        visible: false,
        suggestedSlot: {},
      },
      orderSubmitError: {
        visible: false,
        errors: [],
      },
      voucherFormValid: true,
      checkoutOptionsFormValid: false,
    },
  },
};

export function checkoutState(
  state: CheckoutState = checkoutInitialState.checkoutState,
  action: StoreAction,
): CheckoutState {
  switch (action.type) {
    case StoreActionType.AddCheckoutState: {
      // Two things this case has to get right, because every checkout form
      // dispatches through it on every keystroke.
      //
      // 1. Don't mutate. `{ ...state }` is shallow, so `newState.data` used to
      //    be the SAME object as `state.data`, and writing the key straight
      //    into it edited the previous state in place.
      // 2. Don't report a change that didn't happen. Returning a fresh object
      //    unconditionally makes `getCheckoutState` emit on every dispatch,
      //    even a value-identical one, which is what lets a component that
      //    dispatches from inside a store subscription feed itself forever.
      //
      // #9509 worked around (2) by giving three components their own private
      // 'last dispatched' cache. Those caches then went out of step with the
      // store and stranded the Submit button (#11154). Holding the invariant
      // here instead means no component needs a cache to hold it.
      const nextStatus =
        state.status === 'INVALID_SUBMIT' ? 'INVALID_SUBMIT' : 'MODIFIED';
      if (
        state.data[action.key] === action.value &&
        state.status === nextStatus
      ) {
        return state;
      }
      return {
        ...state,
        data: { ...state.data, [action.key]: action.value },
        status: nextStatus,
      };
    }
    case StoreActionType.CheckoutInvalidSubmit: {
      const newState = { ...state };
      newState.status = 'INVALID_SUBMIT';
      return newState;
    }
    case StoreActionType.ClearCheckoutState:
      return { ...checkoutInitialState.checkoutState };
    case StoreActionType.ToggleOffersUnavailable: {
      const newState = { ...state };
      if (action.display) {
        newState.data.offerAvailabilityErrors = {
          visible: action.display,
          offers: action.offers,
        };
      } else {
        newState.data.offerAvailabilityErrors = {
          visible: action.display,
          offers: [],
        };
      }
      return newState;
    }
    case StoreActionType.ToggleOffersExceeded: {
      const newState = { ...state };
      if (action.display) {
        newState.data.ReservableOffersExceededErrors = {
          visible: action.display,
          offers: action.offers,
        };
      } else {
        newState.data.ReservableOffersExceededErrors = {
          visible: action.display,
          offers: [],
        };
      }
      return newState;
    }
    case StoreActionType.ToggleOffersReserved: {
      const newState = { ...state };
      if (action.display) {
        newState.data.OffersAlreadyReservedErrors = {
          visible: action.display,
          suggestedSlot: action.suggestedSlot,
        };
      } else {
        newState.data.OffersAlreadyReservedErrors = {
          visible: action.display,
          suggestedSlot: [],
        };
      }
      return newState;
    }
    case StoreActionType.ToggleOffersOutOfStock: {
      const newState = { ...state };
      if (action.display) {
        newState.data.offerOutOfStockErrors = {
          visible: action.display,
          offers: action.offers,
        };
      } else {
        newState.data.offerOutOfStockErrors = {
          visible: action.display,
          offers: [],
        };
      }
      return newState;
    }
    case StoreActionType.UpdateVoucher:
      return { ...state, data: { ...state.data, voucherValidation: null } };
    case StoreActionType.ValidateVoucherSuccess:
      return {
        ...state,
        data: {
          ...state.data,
          voucherValidation: true,
          voucherDiscount: action.voucherDiscount,
          voucherDiscountType: action.voucherDiscountType,
          voucherUseType: action.voucherUseType,
          voucherOrderMinAmount: action.orderMinAmount,
          voucherFormattedOrderMinAmount: action.formattedOrderMinAmount,
          voucherHasScheduleRestriction: action.hasScheduleRestriction,
          voucherHasCategoryRestriction: action.hasCategoryRestriction,
        },
      };
    case StoreActionType.ValidateVoucherFailed:
      return { ...state, data: { ...state.data, voucherValidation: false } };
    case StoreActionType.ToggleUnavailableDeliveryTimeError: {
      const newState = { ...state };
      if (action.visible) {
        newState.data.slotUnavailableError = {
          visible: action.visible,
          suggestedSlot: action.suggestedSlot,
          message: action.message,
        };
      } else {
        newState.data.slotUnavailableError = {
          visible: action.visible,
          suggestedSlot: {},
        };
      }
      return newState;
    }
    case StoreActionType.ToggleSameDayOrderingDisabled: {
      const newState = { ...state };
      if (action.display) {
        newState.data.sameDayOrderingErrors = {
          visible: action.display,
          errors: action.errors,
        };
      } else {
        newState.data.sameDayOrderingErrors = {
          visible: action.display,
          errors: [],
        };
      }
      return newState;
    }
    case StoreActionType.ToggleOrderSubmitError: {
      const newState = { ...state };
      if (action.display) {
        newState.data.orderSubmitError = {
          visible: action.display,
          errors: action.errors,
        };
      } else {
        newState.data.orderSubmitError = {
          visible: action.display,
          errors: [],
        };
      }
      return newState;
    }
    case StoreActionType.CheckExistingOrderSuccess: {
      const newState = { ...state };
      const isAdminOrderUpdate = window.location.href.includes('/orders/');
      if (action.order.voucherCode && isAdminOrderUpdate) {
        newState.data.voucherDiscount = action.order.voucherDiscount;
        newState.data.voucherDiscountType = action.order.voucherDiscountType;
        newState.data.voucherValidation = true;
      }
      // Deliberately does NOT clear voucherValidation/voucherDiscount* when
      // action.order.voucherCode is undefined: the backend omits voucherCode
      // via @JsonInclude(NON_NULL) on orders that don't carry one yet, and
      // this re-fetch runs after every order update (delivery method, tip,
      // wishTime, ...), not just voucher ones. Clearing here raced the
      // voucher PUT and made a just-applied discount flash and disappear.
      // UpdateVoucher already resets voucherValidation to null up front when
      // the user starts a new voucher action (apply or remove), which is the
      // correct place for this reset.
      return newState;
    }
  }
  return state;
}

const reducerCheckoutData: (
  state: CheckoutData,
  action: StoreAction,
) => CheckoutData = combineReducers({
  checkoutState,
});

export function checkoutStateReducer(
  state: CheckoutData = checkoutInitialState,
  action: StoreAction,
): CheckoutData {
  return reducerCheckoutData(state, action);
}

// EOF: ORDER META

// COOKIES STATE

export interface CookieData {
  cookieState: CookieState;
}

export interface CookieState {
  status: 'INITIAL' | 'UNSET' | 'ACCEPT' | 'REJECT' | 'VISITED';
}

export const cookieInitialState: CookieData = {
  cookieState: {
    status: 'INITIAL',
  },
};

export function cookieState(
  state: CookieState = cookieInitialState.cookieState,
  action: StoreAction,
): CookieState {
  switch (action.type) {
    case StoreActionType.HideCookieMessage:
      const newState = { ...state };
      newState.status = 'VISITED';
      return newState;
    case StoreActionType.InitCookieMessage:
      return { ...state, status: action.cookieState };
    case StoreActionType.AcceptCookie:
      return { ...state, status: 'ACCEPT' };
    case StoreActionType.RejectCookie:
      return { ...state, status: 'REJECT' };
  }
  return state;
}

const reducerCookieData: (
  state: CookieData,
  action: StoreAction,
) => CookieData = combineReducers({
  cookieState,
});

export function cookieStateReducer(
  state: CookieData = cookieInitialState,
  action: StoreAction,
): CookieData {
  return reducerCookieData(state, action);
}

// EOF: COOKIES STATE

// ZONE STATE

export interface ZoneData {
  zoneState: ZoneState;
}

export interface ZoneState {
  status:
    | 'INITIAL'
    | 'DELIVERABLE'
    | 'NOTDELIVERABLE_ADDRESS'
    | 'NOTDELIVERABLE_POSTCODE'
    | 'FAILED'
    | 'FETCHING_GEOCODE';
  settings: any;
}

export const zoneInitialState: ZoneData = {
  zoneState: {
    status: 'INITIAL',
    settings: {},
  },
};

export function zoneState(
  state: ZoneState = zoneInitialState.zoneState,
  action: StoreAction,
): ZoneState {
  const newState = { ...state };
  switch (action.type) {
    case StoreActionType.GetZonePerZipcodeSuccess:
      newState.status = 'DELIVERABLE';
      newState.settings = action.zone;
      return newState;
    case StoreActionType.GetZonePerZipcodeFail:
      newState.status = 'NOTDELIVERABLE_POSTCODE';
      newState.settings = {};
      return newState;
    // The GeocodeAddress request is read straight off the pipeline; it is fresh
    // by construction, since every producer stamps the token into order meta in
    // the same synchronous dispatch batch as the GeocodeAddress it names.
    case StoreActionType.GeocodeAddress:
      newState.status = 'FETCHING_GEOCODE';
      newState.settings = {};
      return newState;
    // Resolutions can land out of order, and this reducer cannot read
    // orderMeta.data.addressId to tell a fresh resolution from a superseded one
    // (zoneState is a separate `forFeature` slice), so the staleness decision is
    // made once in the effects layer and arrives here pre-judged.
    //
    // NOTE: this handles staleness, not per-leg readiness. FETCHING_GEOCODE is a
    // single global latch, not one per leg — in Pickup & Drop-off, whichever leg
    // settles first releases it while the other may still be legitimately in
    // flight, so a deferred GetAssociatedZone can run once against pre-update
    // coordinates. It is transient (the next leg's resolution reconverges the
    // zone), not a stuck state. A per-leg latch is tracked separately.
    case StoreActionType.ZoneGeocodeSettled:
      newState.status = 'INITIAL';
      newState.settings = {};
      return newState;
    case StoreActionType.ZoneGeocodeAddressFailed:
      if (newState.status === 'NOTDELIVERABLE_ADDRESS') {
        return state;
      }
      newState.status = 'NOTDELIVERABLE_ADDRESS';
      newState.settings = {};
      return newState;
    case StoreActionType.GetAssociatedZoneSuccess:
      if (!action.zoneData.type) {
        newState.status = 'NOTDELIVERABLE_POSTCODE';
        newState.settings = {};
      } else {
        newState.status = 'DELIVERABLE';
        newState.settings = action.zoneData;
      }
      return newState;
    case StoreActionType.ClearZonePerZipcode:
      return zoneInitialState.zoneState;
  }
  return state;
}

const reducerZoneData: (state: ZoneData, action: StoreAction) => ZoneData =
  combineReducers({
    zoneState,
  });

export function zoneStateReducer(
  state: ZoneData = zoneInitialState,
  action: StoreAction,
): ZoneData {
  return reducerZoneData(state, action);
}

// EOF: ZONE STATE

// CURRENT SELECTED CAT ON CATALOG PAGE STATE

export interface CurrentCatData {
  currentCategoryState: CurrentCategoryState;
}

export interface CurrentCategoryState {
  selectedCategory: any;
  selectedTopLevelCategory: any;
}

export const currentCatInitialState: CurrentCatData = {
  currentCategoryState: {
    selectedCategory: null,
    selectedTopLevelCategory: -1,
  },
};

export function currentCategoryState(
  state: CurrentCategoryState = currentCatInitialState.currentCategoryState,
  action: StoreAction,
): CurrentCategoryState {
  const newState = { ...state };
  switch (action.type) {
    case StoreActionType.SetCurrentSelectedCategory:
      newState.selectedCategory = action.selectedCategory;
      return newState;
    case StoreActionType.SetCurrentSelectedTopCategory:
      newState.selectedTopLevelCategory = action.selectedTopCategory;
      return newState;
  }
  return state;
}

const reducerCurrentCatData: (
  state: CurrentCatData,
  action: StoreAction,
) => CurrentCatData = combineReducers({
  currentCategoryState,
});

export function currentCatStateReducer(
  state: CurrentCatData = currentCatInitialState,
  action: StoreAction,
): CurrentCatData {
  return reducerCurrentCatData(state, action);
}

// EOF: CURRENT SELECTED CAT ON CATALOG PAGE STATE

// STORE PROMOTION RULES

export interface StoreRules {
  storeRulesState: StoreRulesState;
}

export interface StoreRulesState {
  storeRules: any;
  request?: {
    storeId: number;
    langId: string;
    referenceDate: string;
    referenceTime?: string;
  };
}

export const storeRulesInitialState: StoreRules = {
  storeRulesState: {
    storeRules: [],
  },
};

export function storeRulesState(
  state: StoreRulesState = storeRulesInitialState.storeRulesState,
  action: StoreAction,
): StoreRulesState {
  const newState = { ...state };
  switch (action.type) {
    case StoreActionType.GetStoreRules:
      return {
        ...state,
        request: {
          storeId: action.storeId,
          langId: action.langId,
          referenceDate: action.referenceDate,
          referenceTime: action.referenceTime,
        },
      };
    case StoreActionType.GetStoreRulesSuccess:
      newState.storeRules = action.rules;
      return newState;
    case StoreActionType.GetStoreRulesFail:
      newState.storeRules = storeRulesInitialState.storeRulesState.storeRules;
      return newState;
  }
  return state;
}

const reducerStoreRules: (
  state: StoreRules,
  action: StoreAction,
) => StoreRules = combineReducers({
  storeRulesState,
});

export function storeRulesReducer(
  state: StoreRules = storeRulesInitialState,
  action: StoreAction,
): StoreRules {
  return reducerStoreRules(state, action);
}

// EOF: STORE PROMOTION RULES

// Customer Social Login

export interface SocialLoginState {
  userId: number;
  tokens: Tokens;
  billingStreetAddress?: string;
  billingHouseNo?: string;
  billingPostCode?: string;
  billingCity?: string;
  billingState?: string;
  billingCountryId?: number;
  billingVatNumber?: string;
  buzzerNumber?: string;
  socialAuthStatus: 'INITIAL' | 'LOADING' | 'SUCCESS' | 'FAILED';
  /**
   * The backend's error code for a `FAILED` sign-in, when it sent one (card
   * #11627). Kept through the `SocialLogout` the failure triggers, because the
   * standalone sign-in screen unmounts its panel while the sign-in runs and
   * mounts a new one only after that logout. Cleared by the next attempt and
   * by `SocialLoginErrorDismissed`.
   */
  socialAuthErrorCode?: string;
}

export interface CustomerDetailsUpdateState {
  userId: number;
  email: string;
  userName: string;
  phoneNumber: string;
  tokens: Tokens;
  billingStreetAddress?: string;
  billingHouseNo?: string;
  billingPostCode?: string;
  billingCity?: string;
  billingState?: string;
  billingCountryId?: number;
  billingVatNumber?: string;
  buzzerNumber?: string;
  customerDetailsUpdateStatus: 'INITIAL' | 'LOADING' | 'SUCCESS' | 'FAILED';
}

export interface SocialAuth {
  socialLoginState: SocialLoginState;
}

export interface CustomerDetailsUpdate {
  customerDetailsUpdateState: CustomerDetailsUpdateState;
}

export const socialLoginInitialState: SocialAuth = {
  socialLoginState: {
    userId: -1,
    tokens: {
      jwt: null,
      refreshToken: null,
    },
    billingStreetAddress: '',
    billingHouseNo: '',
    billingPostCode: '',
    billingCity: '',
    billingState: '',
    billingCountryId: null,
    billingVatNumber: '',
    buzzerNumber: '',
    socialAuthStatus: 'INITIAL',
  },
};

export const customerDetailsUpdateInitialState: CustomerDetailsUpdate = {
  customerDetailsUpdateState: {
    userId: -1,
    email: '',
    userName: '',
    phoneNumber: '',
    tokens: {
      jwt: null,
      refreshToken: null,
    },
    billingStreetAddress: '',
    billingHouseNo: '',
    billingPostCode: '',
    billingCity: '',
    billingState: '',
    billingCountryId: null,
    billingVatNumber: '',
    buzzerNumber: '',
    customerDetailsUpdateStatus: 'INITIAL',
  },
};

export function socialLoginState(
  socialAuthState = socialLoginInitialState.socialLoginState,
  action: StoreAction,
): SocialLoginState {
  switch (action.type) {
    case StoreActionType.SocialLogin:
    case StoreActionType.SocialLoginLoading:
      return {
        ...socialLoginInitialState.socialLoginState,
        socialAuthStatus: 'LOADING',
      };
    case StoreActionType.SocialLoginSuccess:
      return {
        userId: action.response.userId,
        tokens: action.response.tokens,
        billingStreetAddress: action.response.billingStreetAddress,
        billingHouseNo: action.response.billingHouseNo,
        billingPostCode: action.response.billingPostCode,
        billingCity: action.response.billingCity,
        billingState: action.response.billingState,
        billingCountryId: action.response.billingCountryId,
        billingVatNumber: action.response.billingVatNumber,
        buzzerNumber: action.response.buzzerNumber,
        socialAuthStatus: 'SUCCESS',
      };
    case StoreActionType.SocialLoginFailed:
      return {
        ...socialLoginInitialState.socialLoginState,
        socialAuthStatus: 'FAILED',
        socialAuthErrorCode: action.errorCode,
      };
    // Card #11708: the sign-in was abandoned, so there is none in progress any
    // more and the status says so. Deliberately not added to any of the other
    // reducers that handle `SocialLogout` — keeping a still-valid session's
    // tokens, profile, loyalty and orders is the whole reason the action is
    // its own (see `SocialLoginCancelled`).
    case StoreActionType.SocialLoginCancelled:
      return { ...socialLoginInitialState.socialLoginState };
    case StoreActionType.SocialLogout:
      // Card #11627: the logout a failed sign-in triggers keeps its reason.
      return socialAuthState.socialAuthStatus === 'FAILED' &&
        socialAuthState.socialAuthErrorCode
        ? {
            ...socialLoginInitialState.socialLoginState,
            socialAuthErrorCode: socialAuthState.socialAuthErrorCode,
          }
        : { ...socialLoginInitialState.socialLoginState };
    case StoreActionType.SocialLoginErrorDismissed: {
      const { socialAuthErrorCode, ...withoutReason } = socialAuthState;
      return withoutReason;
    }
    default:
      return socialAuthState;
  }
}

export function customerDetailsUpdateState(
  customerDetailUpdateState = customerDetailsUpdateInitialState.customerDetailsUpdateState,
  action: StoreAction,
): CustomerDetailsUpdateState {
  switch (action.type) {
    case StoreActionType.CustomerDetailsUpdate:
      return {
        ...customerDetailsUpdateInitialState.customerDetailsUpdateState,
        customerDetailsUpdateStatus: 'LOADING',
      };
    case StoreActionType.CustomerDetailsUpdateSuccess:
      return {
        userId: action.response.userId,
        userName: action.response.userName,
        phoneNumber: action.response.phoneNumber,
        tokens: action.response.tokens,
        email: action.response.email,
        billingStreetAddress: action.response.billingStreetAddress,
        billingHouseNo: action.response.billingHouseNo,
        billingPostCode: action.response.billingPostCode,
        billingCity: action.response.billingCity,
        billingState: action.response.billingState,
        billingCountryId: action.response.billingCountryId,
        billingVatNumber: action.response.billingVatNumber,
        buzzerNumber: action.response.buzzerNumber,
        customerDetailsUpdateStatus: 'SUCCESS',
      };
    case StoreActionType.CustomerDetailsUpdateFailed:
      return {
        ...customerDetailsUpdateInitialState.customerDetailsUpdateState,
        customerDetailsUpdateStatus: 'FAILED',
      };
    case StoreActionType.SocialLogout:
      return {
        ...customerDetailsUpdateInitialState.customerDetailsUpdateState,
      };
    default:
      return customerDetailUpdateState;
  }
}

export function tokens(
  tokensState = socialLoginInitialState.socialLoginState.tokens ||
    customerDetailsUpdateInitialState.customerDetailsUpdateState.tokens,
  action: StoreAction,
): Tokens {
  switch (action.type) {
    case StoreActionType.SocialLoginSuccess:
    case StoreActionType.CustomerDetailsUpdateSuccess:
      return { ...action.response.tokens };
    case StoreActionType.SocialLoginFailed:
    case StoreActionType.CustomerDetailsUpdateFailed:
    case StoreActionType.SocialLogout:
      return { ...socialLoginInitialState.socialLoginState.tokens };
    default:
      return tokensState;
  }
}

const reducerSocialLogin: (
  state: SocialAuth,
  action: StoreAction,
) => SocialAuth = combineReducers({
  socialLoginState,
  tokens,
});

export function socialLoginReducer(
  state: SocialAuth = socialLoginInitialState,
  action: StoreAction,
): SocialAuth {
  return reducerSocialLogin(state, action);
}

const reducerCustomerDetailsUpdate: (
  state: CustomerDetailsUpdate,
  action: StoreAction,
) => CustomerDetailsUpdate = combineReducers({
  customerDetailsUpdateState,
});

export function customerDetailsUpdateReducer(
  state: CustomerDetailsUpdate = customerDetailsUpdateInitialState,
  action: StoreAction,
): CustomerDetailsUpdate {
  return reducerCustomerDetailsUpdate(state, action);
}

// EOF : Customer Details Update

// Customer Loyalty
export interface CustomerLoyaltyState {
  status: 'INITIAL' | 'LOADING' | 'SUCCESS' | 'FAILED';
  data: CustomerLoyalty;
}

export interface CustomerLoyaltyStateData {
  customerLoyaltyState: CustomerLoyaltyState;
}

export const customerLoyaltyInitialState: CustomerLoyaltyStateData = {
  customerLoyaltyState: {
    status: 'INITIAL',
    data: null,
  },
};

export function customerLoyaltyState(
  state: CustomerLoyaltyState = customerLoyaltyInitialState.customerLoyaltyState,
  action: StoreAction,
): CustomerLoyaltyState {
  switch (action.type) {
    case StoreActionType.LoadCustomerLoyalty:
      return { ...state, status: 'LOADING' };
    case StoreActionType.LoadCustomerLoyaltySuccess:
      return { ...state, data: action.loyalty, status: 'SUCCESS' };
    case StoreActionType.LoadCustomerLoyaltyFailed:
      return { ...state, data: null, status: 'FAILED' };
    case StoreActionType.SocialLogout:
      return { ...state, ...customerLoyaltyInitialState.customerLoyaltyState };
    default:
      return state;
  }
}

const reducerCustomerLoyalty: (
  state: CustomerLoyaltyStateData,
  action: StoreAction,
) => CustomerLoyaltyStateData = combineReducers({
  customerLoyaltyState,
});

export function customerLoyaltyReducer(
  state: CustomerLoyaltyStateData = customerLoyaltyInitialState,
  action: StoreAction,
): CustomerLoyaltyStateData {
  return reducerCustomerLoyalty(state, action);
}

// EOF : Customer Loyalty

// Customer Loyalty Transactions

export interface CustomerLoyaltyTransactionsState {
  status: 'INITIAL' | 'LOADING' | 'SUCCESS' | 'FAILED';
  data: Page<CustomerLoyaltyTrasaction>;
}

export interface CustomerLoyaltyTransactionsStateData {
  customerLoyaltyTransactionsState: CustomerLoyaltyTransactionsState;
}

export const customerLoyaltyTransactionsInitialState: CustomerLoyaltyTransactionsStateData =
  {
    customerLoyaltyTransactionsState: {
      status: 'INITIAL',
      data: null,
    },
  };

export function customerLoyaltyTransactionsState(
  state: CustomerLoyaltyTransactionsState = customerLoyaltyTransactionsInitialState.customerLoyaltyTransactionsState,
  action: StoreAction,
): CustomerLoyaltyTransactionsState {
  switch (action.type) {
    case StoreActionType.LoadCustomerLoyaltyTransactions:
      return { ...state, status: 'LOADING' };
    case StoreActionType.LoadCustomerLoyaltyTransactionsSuccess:
      return { data: action.transactionPage, status: 'SUCCESS' };
    case StoreActionType.LoadCustomerLoyaltyTransactionsFailed:
      return { data: null, status: 'FAILED' };
    case StoreActionType.SocialLogout:
    case StoreActionType.LoadCustomerLoyaltySuccess:
      return {
        ...customerLoyaltyTransactionsInitialState.customerLoyaltyTransactionsState,
      };
    default:
      return state;
  }
}

const reducerCustomerLoyaltyTransactions: (
  state: CustomerLoyaltyTransactionsStateData,
  action: StoreAction,
) => CustomerLoyaltyTransactionsStateData = combineReducers({
  customerLoyaltyTransactionsState,
});

export function customerLoyaltyTransactionsReducer(
  state: CustomerLoyaltyTransactionsStateData = customerLoyaltyTransactionsInitialState,
  action: StoreAction,
): CustomerLoyaltyTransactionsStateData {
  return reducerCustomerLoyaltyTransactions(state, action);
}

// EOF : Customer Loyalty Transactions

// Customer Profile
export interface CustomerProfileState {
  status: 'INITIAL' | 'LOADING' | 'SUCCESS' | 'FAILED';
  updateStatus: 'INITIAL' | 'UPDATING' | 'SUCCESS' | 'FAILED';
  profileUpdateStatus: 'INITIAL' | 'UPDATING' | 'SUCCESS' | 'FAILED';
  addressUpdateStatus: 'INITIAL' | 'UPDATING' | 'SUCCESS' | 'FAILED';
  data: CustomerProfile;
  profilePictureSalt?: string;
}

export interface CustomerProfileStateData {
  customerProfileState: CustomerProfileState;
}

function salt(): string {
  return Math.random().toString(36).substring(2, 7);
}

export const customerProfileInitialState: CustomerProfileStateData = {
  customerProfileState: {
    status: 'INITIAL',
    updateStatus: 'INITIAL',
    profileUpdateStatus: 'INITIAL',
    addressUpdateStatus: 'INITIAL',
    data: null,
    profilePictureSalt: '',
  },
};

export function customerProfileState(
  state: CustomerProfileState = customerProfileInitialState.customerProfileState,
  action: StoreAction,
): CustomerProfileState {
  switch (action.type) {
    case StoreActionType.LoadCustomerProfile:
      return { ...state, status: 'LOADING' };
    case StoreActionType.LoadCustomerProfileSuccess:
      return { ...state, data: action.customer, status: 'SUCCESS' };
    case StoreActionType.LoadCustomerProfileFailed:
      return { ...state, data: null, status: 'FAILED' };
    case StoreActionType.UpdateCustomerProfile:
    case StoreActionType.DeleteCustomerProfile:
    case StoreActionType.UploadCustomerProfilePicture:
    case StoreActionType.DeleteCustomerProfilePicture:
      return {
        ...state,
        updateStatus: 'UPDATING',
        profileUpdateStatus: 'UPDATING',
      };
    case StoreActionType.UpdateCustomerAddress:
    case StoreActionType.DeleteCustomerAddress:
      return {
        ...state,
        updateStatus: 'UPDATING',
        addressUpdateStatus: 'UPDATING',
      };
    case StoreActionType.UpdateCustomerProfileSuccess:
    case StoreActionType.DeleteCustomerProfilePictureSuccess:
      return {
        ...state,
        data: action.customer,
        updateStatus: 'SUCCESS',
        profileUpdateStatus: 'SUCCESS',
      };
    case StoreActionType.UpdateCustomerAddressSuccess:
    case StoreActionType.DeleteCustomerAddressSuccess:
      return {
        ...state,
        data: action.customer,
        updateStatus: 'SUCCESS',
        addressUpdateStatus: 'SUCCESS',
      };
    case StoreActionType.UploadCustomerProfilePictureSuccess:
      return {
        ...state,
        data: action.customer,
        profilePictureSalt: salt(),
        updateStatus: 'SUCCESS',
        profileUpdateStatus: 'SUCCESS',
      };
    case StoreActionType.UpdateCustomerProfileFailed:
    case StoreActionType.DeleteCustomerProfileFailed:
    case StoreActionType.UploadCustomerProfilePictureFailed:
    case StoreActionType.DeleteCustomerProfilePictureFailed:
      return {
        ...state,
        updateStatus: 'FAILED',
        profileUpdateStatus: 'FAILED',
      };
    case StoreActionType.UpdateCustomerAddressFailed:
    case StoreActionType.DeleteCustomerAddressFailed:
      return {
        ...state,
        updateStatus: 'FAILED',
        addressUpdateStatus: 'FAILED',
      };
    case StoreActionType.SocialLogout:
      return { ...customerProfileInitialState.customerProfileState };
    default:
      return state;
  }
}

const reducerCustomerProfile: (
  state: CustomerProfileStateData,
  action: StoreAction,
) => CustomerProfileStateData = combineReducers({
  customerProfileState,
});

export function customerProfileReducer(
  state: CustomerProfileStateData = customerProfileInitialState,
  action: StoreAction,
): CustomerProfileStateData {
  return reducerCustomerProfile(state, action);
}

// EOF : Customer Profile Picture

// CustomerInfo details for order capture @ admin UI

export interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  customerId?: number;
}

export interface CustomerInfoData {
  customerInfo: CustomerInfo;
}

export const customerInfoInitialState: CustomerInfoData = {
  customerInfo: {
    name: null,
    email: null,
    phone: null,
    customerId: null,
  },
};

export function customerInfo(
  state: CustomerInfo = customerInfoInitialState.customerInfo,
  action: StoreAction,
): CustomerInfo {
  switch (action.type) {
    case StoreActionType.SetCustomerInfo:
      return {
        name: action.info.name,
        email: action.info.email,
        phone: action.info.phone,
        customerId: action.info.customerId ?? null,
      };
    case StoreActionType.SubmitOrderSuccess:
    case StoreActionType.ClearCustomerInfo:
      return { email: null, name: null, phone: null, customerId: null };
    default:
      return state;
  }
}

const reducerCustomerInfoData: (
  state: CustomerInfoData,
  action: StoreAction,
) => CustomerInfoData = combineReducers({
  customerInfo,
});

export function customerInfoReducer(
  state: CustomerInfoData = customerInfoInitialState,
  action: StoreAction,
): CustomerInfoData {
  return reducerCustomerInfoData(state, action);
}
// EOF : CustomerInfo details for order capture @ admin UI

// GetCombinedOrder
export interface CombinedOrderState {
  combinedOrderState: CartState;
}

export const combinedOrderInitialState: CombinedOrderState = {
  combinedOrderState: {
    status: 'INITIAL',
    data: {
      location: -1,
      uuid: null,
      orderItems: [],
      createdAt: '',
    },
    errorCode: null,
  },
};

export function combinedOrderState(
  state: CartState = cartInitialState.cartState,
  action: StoreAction,
): CartState {
  switch (action.type) {
    case StoreActionType.InitializeOrder:
      return { ...state, status: 'LOADING' };
    case StoreActionType.InitializeOrderSuccess:
      return {
        status: 'LOADED',
        data: action.order,
      };
    case StoreActionType.InitializeOrderFailed:
      return { ...selectedStoreInitialState[''], status: 'FAILED' };
    case StoreActionType.InitializeCartState:
      return { ...selectedStoreInitialState[''], status: 'INITIAL' };
    case StoreActionType.GetCombinedOrder:
      return { ...state, status: 'LOADING' };
    case StoreActionType.GetCombinedOrderSuccess:
      return {
        status: 'LOADED',
        data: action.order,
      };
    case StoreActionType.GetCombinedOrderFailed:
      return {
        ...selectedStoreInitialState[''],
        status: 'FAILED',
        errorCode: action.errorCode,
      };
  }
  return state;
}

const reducercombinedOrders: (
  state: CombinedOrderState,
  action: StoreAction,
) => CombinedOrderState = combineReducers({
  combinedOrderState,
});

export function combinedOrderReducer(
  state: CombinedOrderState = combinedOrderInitialState,
  action: StoreAction,
): CombinedOrderState {
  return reducercombinedOrders(state, action);
}

// Standalone Payment
export interface StandalonePaymentOrderState {
  status:
    | 'INITIAL'
    | 'LOADING'
    | 'CHECKEXISTING'
    | 'REFRESH'
    | 'REFRESHED'
    | 'LOADED'
    | 'ITEMADDING'
    | 'ITEMADDED'
    | 'ITEMREMOVING'
    | 'ITEMREMOVED'
    | 'RULE_OFFERS_REMOVED'
    | 'FAILED'
    | 'ITEMADDFAILED'
    | 'ITEMREMOVEFAILED'
    | 'ITEMUPDATING'
    | 'ITEMUPDATED'
    | 'ITEMUPDATEFAILED'
    | 'ITEMSEARCHSUCCESS'
    | 'ITEMSEARCHFAILED'
    | 'INITIATE_PAYMENT_SUCCESS'
    | 'INITIATE_PAYMENT_FAILED'
    | 'COMPLETE_PAYMENT_SUCCESS'
    | 'ORDERUPDATING'
    | 'ORDERUPDATEFAILED'
    | 'CHECKEXISTINGFAILED'
    | 'ORDERSUBMITTED'
    | 'ORDERSUBMITFAILED'
    | 'INIT_ONLINE_PAYMENT'
    | 'FINISHED_ONLINE_PAYMENT'
    | 'RULE_OFFERS_ADDED'
    | 'RULE_OFFERS_ADD_FAILED';
  data: StandalonePaymentOrder;
  formattedOpenBalance: string;
  formattedTotalPaid: string;
  openBalance: number;
  standalonePaymentOrders: StandalonePaymentOrder[];
  totalPaid: number;
  openStandalonePaymentOrder: StandalonePaymentOrder;
  standalonePaymentItems: {
    standalonePaymentItemList: StandalonePaymentItems[];
  };
}

export interface StandalonePaymentItems {
  amount: number;
  id: number;
  itemReference: string;
  quantity: number;
  standalonePaymentId: string;
}
export interface StandalonePaymentState {
  standalonePaymentOrderState: StandalonePaymentOrderState;
}

export const standalonePaymentInitialState: StandalonePaymentState = {
  standalonePaymentOrderState: {
    status: 'INITIAL',
    data: {
      id: '',
      storeId: '',
      order: '',
      tapId: '',
      paymentType: '',
      amountReference: 0,
      amountSelected: 0,
      amountTip: 0,
      divideBillBy: 0,
      amountTotal: 0,
      tipPercentage: 0,
      currency: 0,
      customerName: '',
      location: 0,
      deviceIdentifier: '',
      createdAt: '',
      updatedAt: '',
      paymentTransactionId: '',
      paymentMethod: '',
      paymentProviderIdentifier: '',
      referenceType: '',
      paymentStatus: '',
      possibleTips: [],
    },
    formattedOpenBalance: '',
    formattedTotalPaid: '',
    openBalance: 0,
    standalonePaymentOrders: [],
    totalPaid: 0,
    openStandalonePaymentOrder: null,
    standalonePaymentItems: {
      standalonePaymentItemList: [],
    },
  },
};

export function standalonePaymentOrderState(
  state: StandalonePaymentOrderState = standalonePaymentInitialState.standalonePaymentOrderState,
  action: StoreAction,
): StandalonePaymentOrderState {
  switch (action.type) {
    case StoreActionType.InitializeCartState:
      return {
        ...standalonePaymentInitialState['standalonePaymentOrderState'],
        status: 'INITIAL',
      };
    case StoreActionType.CreateStandalonePayment:
      return { ...state, status: 'LOADING' };
    case StoreActionType.CreateStandalonePaymentSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: action.standaloneOrder,
      };
    case StoreActionType.CreateStandalonePaymentFailed:
      return {
        ...standalonePaymentInitialState['standalonePaymentOrderState'],
        status: 'FAILED',
      };
    case StoreActionType.UpdateStandalonePayment:
      return { ...state, status: 'ITEMUPDATING' };
    case StoreActionType.UpdateStandalonePaymentSuccess:
      return { ...state, status: 'ITEMUPDATED', data: action.standaloneOrder };
    case StoreActionType.UpdateStandalonePaymentFailed:
      return { ...state, status: 'ITEMUPDATEFAILED' };
    case StoreActionType.CustomerStandalonePaymentSearchSuccess:
      return { ...state, ...action.res, status: 'ITEMSEARCHSUCCESS' };
    case StoreActionType.CustomerStandalonePaymentSearchFailed:
      return { ...state, status: 'ITEMSEARCHFAILED' };
    case StoreActionType.InitiateStandalonePaymentSuccess:
      return {
        ...state,
        data: action.standaloneOrder,
        status: 'INITIATE_PAYMENT_SUCCESS',
      };
    case StoreActionType.InitiateStandalonePaymentFailed:
      return { ...state, status: 'INITIATE_PAYMENT_FAILED' };
    case StoreActionType.CompleteStandalonePaymentSuccess:
      return {
        ...state,
        data: action.standaloneOrder,
        status: 'COMPLETE_PAYMENT_SUCCESS',
      };
  }
  return state;
}

const reducerStandalonePaymentOrders: (
  state: StandalonePaymentState,
  action: StoreAction,
) => StandalonePaymentState = combineReducers({
  standalonePaymentOrderState,
});

export function standalonePaymentOrderReducer(
  state: StandalonePaymentState = standalonePaymentInitialState,
  action: StoreAction,
): StandalonePaymentState {
  return reducerStandalonePaymentOrders(state, action);
}

// MOBILE STORE ALIAS STATE
export interface MobileStoreData {
  mobileStoreState: MobileStoreState;
}

export interface MobileStoreState {
  storeAlias: string;
}

export const mobileStoreInitialState: MobileStoreData = {
  mobileStoreState: {
    storeAlias: '',
  },
};

export function mobileStoreState(
  state: MobileStoreState = mobileStoreInitialState.mobileStoreState,
  action: StoreAction,
): MobileStoreState {
  switch (action.type) {
    case StoreActionType.SetMobileStore:
      return { ...state, storeAlias: action.storeAlias };
    default:
      return state;
  }
}

const reducerMobileStoreData: (
  state: MobileStoreData,
  action: StoreAction,
) => MobileStoreData = combineReducers({
  mobileStoreState,
});

export function mobileStoreReducer(
  state: MobileStoreData = mobileStoreInitialState,
  action: StoreAction,
): MobileStoreData {
  return reducerMobileStoreData(state, action);
}
// EOF : MOBILE STORE ALIAS STATE

//Customer footer height state
export interface CustomerFooterHeightData {
  customerFooterHeightState: CustomerFooterHeightState;
}

export interface CustomerFooterHeightState {
  customerFooterHeight: number;
}

export const customerFooterHeightInitialState: CustomerFooterHeightData = {
  customerFooterHeightState: {
    customerFooterHeight: 83,
  },
};

export function customerFooterHeightState(
  state: CustomerFooterHeightState = customerFooterHeightInitialState.customerFooterHeightState,
  action: StoreAction,
): CustomerFooterHeightState {
  switch (action.type) {
    case StoreActionType.SetCustomerFooterHeight:
      return { ...state, customerFooterHeight: action.customerFooterHeight };
    default:
      return { ...state };
  }
}

const reducerCustomerFooterHeight: (
  state: CustomerFooterHeightData,
  action: StoreAction,
) => CustomerFooterHeightData = combineReducers({
  customerFooterHeightState,
});

export function customerFooterReducer(
  state: CustomerFooterHeightData = customerFooterHeightInitialState,
  action: StoreAction,
): CustomerFooterHeightData {
  return reducerCustomerFooterHeight(state, action);
}
//End Customer footer height state

// Store recents information saved to storage
export interface StoreRecents {
  storeRecents: StoreRecentsState;
}

export interface StoreRecentsState {
  status: 'INITIAL' | 'LOADING' | 'LOADED' | 'FAILED';
  storeRecentsList: StoreRecentsInfo[];
}

export const storeRecentsInitialState: StoreRecents = {
  storeRecents: {
    status: 'INITIAL',
    storeRecentsList: [],
  },
};

export function storeRecents(
  state: StoreRecentsState = storeRecentsInitialState.storeRecents,
  action: StoreAction,
): StoreRecentsState {
  switch (action.type) {
    case StoreActionType.SaveStoreByAlias:
      return { ...state, status: 'LOADING' };
    case StoreActionType.SaveStoreByAliasSuccess: {
      let newState = { ...state };
      const index = newState.storeRecentsList.findIndex(
        (e) => e.storeAlias === action.storeAlias,
      );
      if (index < 0) {
        newState.storeRecentsList.push({
          storeId: action.storeId,
          storeAlias: action.storeAlias,
          storeName: action.storeName,
          storeUrl: action.storeUrl,
          saveMode: action.saveMode,
          storeLogoUrl: action.storeLogoUrl,
          lastOpen: new Date().toUTCString(),
        });
        return { ...newState, status: 'LOADED' };
      } else {
        newState.storeRecentsList[index].lastOpen = new Date().toUTCString();
        return { ...newState, status: 'LOADED' };
      }
    }
    case StoreActionType.SaveStoreByAliasFailed:
      return { ...state, status: 'FAILED' };
    case StoreActionType.UpdateLastOpen: {
      const index = state.storeRecentsList.findIndex(
        (e) => e.storeAlias === action.storeAlias,
      );
      if (index < 0) {
        // The customer never opened this store, so there is no row to bump —
        // and nothing may be minted here. `UpdateLastOpen` is what the store
        // page's automatic recovery dispatches in place of `SaveStoreByAlias`
        // precisely because it cannot add a row (card #11730).
        return state;
      }
      // A fresh list and a fresh row rather than an in-place write: the
      // Recents screen reads this through `getStoreRecentsList`, whose
      // projector returns the list reference, so a mutated row leaves the
      // selector's output identical and an open screen never hears about the
      // bump. The row order is deliberately left alone — the screen sorts by
      // `lastOpen` itself (`store-recents.component.ts`).
      const storeRecentsList = [...state.storeRecentsList];
      storeRecentsList[index] = {
        ...storeRecentsList[index],
        lastOpen: new Date().toUTCString(),
      };
      return { ...state, storeRecentsList };
    }
  }
  return state;
}

const reducerStoreRecents: (
  state: StoreRecents,
  action: StoreAction,
) => StoreRecents = combineReducers({
  storeRecents,
});

export function storesRecentsReducer(
  state: StoreRecents = storeRecentsInitialState,
  action: StoreAction,
): StoreRecents {
  return reducerStoreRecents(state, action);
}

// Customer Orders

export interface CustomerOrdersState {
  status: 'INITIAL' | 'LOADING' | 'SUCCESS' | 'FAILED';
  data: Page<Order>;
}

export interface CustomerOrdersStateData {
  customerOrdersState: CustomerOrdersState;
}

export const customerOrdersInitialState: CustomerOrdersStateData = {
  customerOrdersState: {
    status: 'INITIAL',
    data: null,
  },
};

export function customerOrdersState(
  state: CustomerOrdersState = customerOrdersInitialState.customerOrdersState,
  action: StoreAction,
): CustomerOrdersState {
  switch (action.type) {
    case StoreActionType.LoadCustomerOrders:
      return { ...state, status: 'LOADING' };
    case StoreActionType.LoadCustomerOrdersSuccess:
      return { data: action.orderPage, status: 'SUCCESS' };
    case StoreActionType.LoadCustomerOrdersFailed:
      return { data: null, status: 'FAILED' };
    case StoreActionType.SocialLogout:
    case StoreActionType.LoadCustomerLoyaltySuccess:
      return { ...customerOrdersInitialState.customerOrdersState };
    default:
      return state;
  }
}

const reducerCustomerOrders: (
  state: CustomerOrdersStateData,
  action: StoreAction,
) => CustomerOrdersStateData = combineReducers({
  customerOrdersState,
});

export function customerOrdersReducer(
  state: CustomerOrdersStateData = customerOrdersInitialState,
  action: StoreAction,
): CustomerOrdersStateData {
  return reducerCustomerOrders(state, action);
}

// EOF : Customer Orders
